import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Stack
} from '@mui/material';
import {
  Edit,
  Delete,
  Search,
  Add,
  Visibility,
  Download,
  ArrowUpward,
  Send,
  CheckCircle,
  Refresh,
  FileDownload
} from '@mui/icons-material';
import { Budget, BudgetSummary, BudgetFilters, BudgetStatus } from '../../types';
import { BudgetApiService } from '../../services/budgetApiService';
import { useAuth } from '../../context/AuthContext';
import { BudgetExportModal } from './BudgetExportModal';

const ITEMS_PER_PAGE = 10;

const BudgetListNew: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Estados principales
  const [budgets, setBudgets] = useState<BudgetSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false); // Loading separado para búsqueda
  const [error, setError] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Estados de filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(''); // Agregar debounce
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<string>('');
  const [exportModalOpen, setExportModalOpen] = useState(false);
  
  // Estados de UI
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; budget: BudgetSummary | null }>({
    open: false,
    budget: null
  });

  // Cargar presupuestos
  const loadBudgets = async (pageNum: number, searchTerm: string, status: string) => {
    try {
      setSearchLoading(true);
      setError(null);
      
      const filters: BudgetFilters = {
        limit: ITEMS_PER_PAGE,
        skip: (pageNum - 1) * ITEMS_PER_PAGE,
      };
      
      if (searchTerm) {
        filters.search = searchTerm;
      }
      
      if (status) {
        filters.status = status;
      }
      
      const response = await BudgetApiService.listBudgets(filters);
      setBudgets(response.items);
      setTotalItems(response.total);
      
    } catch (err: any) {
      console.error('Error loading budgets:', err);
      setError(err.response?.data?.detail || 'Error al cargar los presupuestos');
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  // Efecto para cargar datos
  useEffect(() => {
    setLoading(true);
    loadBudgets(currentPage, debouncedSearchTerm, statusFilter);
  }, [currentPage, debouncedSearchTerm, statusFilter]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Manejar eliminación
  const handleDelete = async () => {
    if (!deleteDialog.budget) return;
    
    try {
      await BudgetApiService.deleteBudget(deleteDialog.budget.id);
      setDeleteDialog({ open: false, budget: null });
      loadBudgets(currentPage, debouncedSearchTerm, statusFilter); // Recarga después de eliminar
    } catch (err: any) {
      console.error('Error deleting budget:', err);
      setError(err.response?.data?.detail || 'Error al eliminar el presupuesto');
    }
  };

  // Obtener versión actual - No necesaria para BudgetSummary
  const getCurrentVersion = (budget: BudgetSummary) => {
    return null; // BudgetSummary no tiene versions
  };

  // Calcular total del presupuesto - Usar total ya calculado
  const getBudgetTotal = (budget: BudgetSummary) => {
    return budget.total_amount || 0;
  };

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  if (loading && budgets.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Encabezado, acción principal y buscador en el mismo Card */}
      <Card sx={{ borderRadius: 4, boxShadow: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" px={3} pt={3} pb={2}>
          <Typography
            variant="h5"
            fontWeight={700}
            color="primary.main"
          >
            Presupuestos
          </Typography>
          <Box display="flex" gap={1}>
            {user?.role === 'admin' && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FileDownload />}
                onClick={() => setExportModalOpen(true)}
              >
                Exportar Excel
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => navigate('/budgets/new')}
            >
              Nuevo Presupuesto
            </Button>
          </Box>
        </Box>
        
        <CardContent sx={{ px: 3, pt: 0, pb: 3 }}>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <TextField
              label="Buscar por cliente o código"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {searchLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Search />
                    )}
                  </InputAdornment>
                )
              }}
              sx={{ flex: 1, minWidth: 300, maxWidth: 400 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Estado</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Estado"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value={BudgetStatus.DRAFT}>Borrador</MenuItem>
                <MenuItem value={BudgetStatus.CLOSED}>Cerrado</MenuItem>
                <MenuItem value={BudgetStatus.SOLD}>Vendido</MenuItem>
                <MenuItem value={BudgetStatus.LOST}>Perdido</MenuItem>
              </Select>
            </FormControl>
            
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('');
                setCurrentPage(1);
              }}
            >
              Limpiar
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabla */}
      <Card sx={{ borderRadius: 4, boxShadow: 3, mb: 2 }}>
        <TableContainer>
          <Table sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 700, py: 2, bgcolor: 'primary.main', color: 'common.white', borderTopLeftRadius: 16, border: 0 }}>Proyecto</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, py: 2, bgcolor: 'primary.main', color: 'common.white', border: 0 }}>Cliente</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, py: 2, bgcolor: 'primary.main', color: 'common.white', border: 0 }}>Vendedor</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, py: 2, bgcolor: 'primary.main', color: 'common.white', border: 0 }}>Versión</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, py: 2, bgcolor: 'primary.main', color: 'common.white', border: 0 }}>Fecha</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, py: 2, bgcolor: 'primary.main', color: 'common.white', border: 0 }}>Estado</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, py: 2, bgcolor: 'primary.main', color: 'common.white', borderTopRightRadius: user?.role === 'admin' ? 0 : 16, border: 0 }}>Total</TableCell>
                  {user?.role === 'admin' && (
                    <TableCell align="center" sx={{ fontWeight: 700, py: 2, bgcolor: 'primary.main', color: 'common.white', borderTopRightRadius: 16, border: 0 }}>Acciones</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {budgets.map((budget, idx) => {
                  const currentVersion = getCurrentVersion(budget);
                  const total = getBudgetTotal(budget);

                  // DEBUG: Log ARS total components
                  console.log('Presupuesto', budget.id, {
                    total_instalacion: budget.total_instalacion,
                    descuento_instalacion: budget.descuento_instalacion,
                    total_flete: budget.total_flete,
                    descuento_fletes: budget.descuento_fletes
                  });
                  
                  return (
                    <TableRow 
                      key={budget.id} 
                      sx={{
                        backgroundColor: idx % 2 === 0 ? 'white' : '#f9f9f9',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#e3f2fd' },
                      }}
                      onClick={() => navigate(`/budgets/${budget.id}`)}
                    >
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="medium">
                          {budget.proyecto}
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="center">
                        <Typography variant="body2">
                          {budget.cliente}
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="center">
                        <Typography variant="body2">
                          {budget.current_version_vendedor || budget.vendedor || 'No asignado'}
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="center">
                        <Box>
                          {budget.current_version_number ? (
                            <Typography variant="body2" fontWeight="medium">
                              v{budget.current_version_number}: {budget.current_version_titulo || 'Sin título'}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Sin versiones
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      
                      <TableCell align="center">
                        <Typography variant="body2">
                          {budget.current_version_fecha 
                            ? new Date(budget.current_version_fecha).toLocaleDateString()
                            : new Date(budget.created_at).toLocaleDateString()
                          }
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="center">
                        <Chip
                          label={BudgetApiService.getStatusLabel((budget as any).current_version_estado || budget.status)}
                          color={BudgetApiService.getStatusColor((budget as any).current_version_estado || budget.status)}
                          size="small"
                          sx={((budget as any).current_version_estado || budget.status) === 'sold' ? {
                            backgroundColor: '#4caf50',
                            color: 'white'
                          } : {}}
                        />
                      </TableCell>
                      
                      <TableCell align="center">
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {/* Total USD (ya incluye descuentos por ambiente) */}
                            {budget.total_materiales
                              ? `U$ ${budget.total_materiales.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                              : '-'}
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {/* Total ARS con descuentos aplicados */}
                            {typeof budget.total_instalacion === 'number' && typeof budget.total_flete === 'number'
                              ? (() => {
                                  const totalInst = (budget.total_instalacion || 0) * (1 - (budget.descuento_instalacion || 0) / 100);
                                  const totalFlete = (budget.total_flete || 0) * (1 - (budget.descuento_fletes || 0) / 100);
                                  return `AR$ ${(totalInst + totalFlete).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                                })()
                              : '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      {user?.role === 'admin' && (
                        <TableCell align="center">
                          <IconButton
                            onClick={e => { 
                              e.stopPropagation(); 
                              setDeleteDialog({ open: true, budget }); 
                            }}
                            title="Eliminar presupuesto"
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginación */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3} mb={2} px={3}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
              />
            </Box>
          )}

          {/* Sin resultados */}
          {budgets.length === 0 && !loading && (
            <Box textAlign="center" py={4} px={3}>
              <Typography variant="h6" color="text.secondary">
                No se encontraron presupuestos
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('/budgets/new')}
                sx={{ mt: 2 }}
              >
                Crear Primer Presupuesto
              </Button>
            </Box>
          )}
      </Card>

      {/* Dialog de confirmación de eliminación */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, budget: null })}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar el presupuesto{' '}
            <strong>{deleteDialog.budget?.code}</strong> para{' '}
            <strong>{deleteDialog.budget?.cliente}</strong>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, budget: null })}>
            Cancelar
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* FAB para móvil */}
      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: { xs: 'block', sm: 'none' }
        }}
        onClick={() => navigate('/budgets/new')}
      >
        <Add />
      </Fab>

      {/* Modal de exportación a Excel */}
      <BudgetExportModal 
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
      />
    </Box>
  );
};

export default BudgetListNew;
